import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

import { parseDateWithoutTime } from '../../../../../../../../../../../../shared/helpers/parse-date';
import { textifyDeclaredUnit } from '../../../../../../../../../../../../shared/helpers/textify-numerical-footprint-values-with-their-units';
import { DeclaredUnit } from '../../../../../../../../../../../../shared/interfaces/IComponentDetails';
import { ICalculationDetails } from '../../../../../../../../../../../../shared/interfaces/IFootprint';
import styles from '../../CalculationDetails.module.css';

type GeneralInformationCardProps = {
  calculationDetails?: ICalculationDetails;
};

const GeneralInformationCard = ({ calculationDetails }: GeneralInformationCardProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={classNames('p-4 border-round-2xl flex w-full flex-column gap-5', styles['card'])}
    >
      <h5 className='p-0 m-0 font-medium'>
        {t('calculationDetailsPage.sections.generalInformation.title')}
      </h5>

      <div className='flex flex-column gap-3'>
        <div className='grid grid-nogutter w-full row-gap-3'>
          {/* Declared Unit */}
          <div className='col-12 md:col-3 flex flex-column gap-1 pr-3'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.generalInformation.declaredUnit')}
            </span>
            <div className='flex flex-row gap-1 text-lg'>
              <span>{calculationDetails?.unitary_product_amount}</span>
              <span>{textifyDeclaredUnit(calculationDetails?.declared_unit)}</span>
            </div>
          </div>

          {/* Product Mass */}
          <div className='col-12 md:col-3 flex flex-column gap-1 pr-3'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.generalInformation.productMass')}
            </span>
            {calculationDetails?.product_mass_per_declared_unit ? (
              <div className='flex flex-row gap-1 text-lg'>
                <span>{calculationDetails?.product_mass_per_declared_unit}</span>
                <span>{textifyDeclaredUnit(DeclaredUnit.kilogram)}</span>
              </div>
            ) : (
              <span className='text-lg'>{t('global.notSpecified')}</span>
            )}
          </div>

          {/* Pcf Type */}
          <div className='col-12 md:col-3 flex flex-column gap-1 pr-3'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.generalInformation.pcfType')}
            </span>
            <span className='text-lg'>
              {calculationDetails?.retro_or_prospective_pcf_type ?? t('global.notSpecified')}
            </span>
          </div>

          {/* Partial or Full PCF */}
          <div className='col-12 md:col-3 flex flex-column gap-1 pr-3'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.generalInformation.partialOrFullPcf')}
            </span>
            <span className='text-lg'>{calculationDetails?.partial_full_pcf}</span>
          </div>
        </div>

        <div className='grid grid-nogutter w-full row-gap-3'>
          {/* Reference Period */}
          <div className='col-12 md:col-3 flex flex-column gap-1 pr-3'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.generalInformation.referencePeriod')}
            </span>
            <div className='flex flex-wrap gap-1 text-lg'>
              <span>{parseDateWithoutTime(calculationDetails?.reference_period_start)}</span>
              <span>-</span>
              <span>{parseDateWithoutTime(calculationDetails?.reference_period_end)}</span>
            </div>
          </div>

          {/* Valid Until */}
          <div className='col-12 md:col-3 flex flex-column gap-1 pr-3'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.generalInformation.validUntil')}
            </span>
            <span className='text-lg'>
              {calculationDetails?.validity_period_end
                ? parseDateWithoutTime(calculationDetails.validity_period_end)
                : t('global.notSpecified')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralInformationCard;
